import {TaskChatInputToolbar} from './task-chat-input-toolbar/TaskChatInputToolbar'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'

import type {SelectedAgent} from '@github-ui/agent-sessions-types/agent-identity'
import {ConsecutiveCAPI401sProvider} from '@github-ui/copilot-shared-ui/capi/ConsecutiveCAPI401sContext'
import {ModelPicker} from './ModelPicker'
import {ReasoningEffortPicker} from './ReasoningEffortPicker'
import {AgentSelector} from './AgentSelector'
import {AgentSelectorCaretTrigger} from './AgentSelectorMenuTrigger'
import {EnvironmentPicker} from './environment-strip/EnvironmentPicker'
import {ImageAttachButton} from './ImageAttachButton'
import {McpPermissionModeSelector} from './McpPermissionModeSelector'
import type {TaskComposerController} from './use-task-composer'
import {
  isCustomAgentPickerAvailable,
  isImageAttachShortcutRedundant,
  isSandboxBranchSelectorDisabled,
} from './use-environment-selection'
import {CLOUD_ENVIRONMENT_ID} from './environment-strip/environment-option'
import styles from './TaskComposerToolbar.module.css'

/**
 * The toolbar rendered inside the task input's toolbar slot (`toolbarContent` for the composer,
 * `extraToolbar` for the legacy `ChatInput`). Everything here is identical between the two input
 * primitives, so it is driven entirely by the shared {@link TaskComposerController}.
 */
export function TaskComposerToolbar({controller, meter}: {controller: TaskComposerController; meter?: ReactNode}) {
  const {
    showEnvironmentPicker,
    showCreateTaskStrip,
    selectedEnvironment,
    setSelectedEnvironment,
    showSandboxOption,
    isAhpEnvironment,
    isSandboxEnvironment,
    isEnvironmentSubmitting,
    selectedRepo,
    selectedBranch,
    setSelectedBranch,
    isMcpPermissionGateEnabled,
    mcpPermissionMode,
    setMcpPermissionMode,
    eventType,
    textAreaRef,
    isAtImageLimit,
    isSubAgentsEnabled,
    selectedAgent,
    setSelectedAgent,
    shouldShowModelPicker,
    selectedModelId,
    selectModel,
    isModelSelectionReady,
    vendorFilters,
    isTokenBasedBillingEnabled,
    isReasoningEffortEnabled,
    selectedReasoningEffort,
    setSelectedReasoningEffort,
    supportedReasoningEfforts,
    hideRepoSelector,
    initialNameWithOwner,
    initialBranch,
    branchParam,
    handleRepoSelect,
    currentPrompt,
    resolvedUserLogin,
  } = controller
  const agentSelectorRepositoryProps = selectedRepo
    ? {repo: selectedRepo, branch: selectedBranch || selectedRepo.defaultBranch}
    : {repo: undefined, branch: undefined}

  return (
    <>
      {showEnvironmentPicker && !showCreateTaskStrip && (
        <EnvironmentPicker
          appearance="toolbar"
          selectedEnvironmentId={selectedEnvironment?.id ?? CLOUD_ENVIRONMENT_ID}
          onSelectEnvironment={setSelectedEnvironment}
          includeSandboxOption={showSandboxOption}
          announceUnavailable={false}
          disabled={isEnvironmentSubmitting}
          triggerClassName={styles.pickerButton}
        />
      )}
      {(!isAhpEnvironment || isSandboxEnvironment) && isMcpPermissionGateEnabled && !showCreateTaskStrip && (
        <McpPermissionModeSelector
          selectedMode={mcpPermissionMode}
          onSelectMode={setMcpPermissionMode}
          forceIconOnly={eventType === 'agents_panel'}
          triggerClassName={styles.pickerButton}
        />
      )}
      {showCreateTaskStrip && (
        <div className={styles.extraToolbarActions}>
          {(!isAhpEnvironment || isSandboxEnvironment) && (
            <>
              {/* This shortcut uploads images against the selected repository,
                  which duplicates what the Sandbox draft's own attach menu
                  already offers as local files. */}
              {!isImageAttachShortcutRedundant({
                isSandboxEnvironment,
                hasSelectedRepository: Boolean(selectedRepo),
              }) && (
                <ImageAttachButton
                  textAreaRef={textAreaRef}
                  repositoryId={selectedRepo?.id?.toString()}
                  disabled={!selectedRepo}
                  isAtImageLimit={isAtImageLimit}
                  size="medium"
                />
              )}
              {isMcpPermissionGateEnabled && (
                <McpPermissionModeSelector
                  selectedMode={mcpPermissionMode}
                  onSelectMode={setMcpPermissionMode}
                  forceIconOnly={false}
                  hideLeadingVisual
                  triggerClassName={styles.pickerButton}
                />
              )}
              {isCustomAgentPickerAvailable({isSubAgentsEnabled, isSandboxEnvironment}) && (
                // AgentSelector -> useSubAgents -> useCAPIQuery requires ConsecutiveCAPI401sProvider.
                // The non-env-strip branch gets this provider from TaskChatInputToolbar, but this branch
                // renders AgentSelector directly, so we wrap it here. AgentsView already provides one
                // higher in the tree, but CopilotChatInputPartial does not, so this wrap is
                // load-bearing for those consumers.
                <ConsecutiveCAPI401sProvider>
                  <AgentSelector
                    {...agentSelectorRepositoryProps}
                    repositoryOptional={isSandboxEnvironment}
                    selectedAgent={selectedAgent}
                    onSelectAgent={(agent: SelectedAgent) => setSelectedAgent(agent)}
                    iconOnly
                    triggerComponent={AgentSelectorCaretTrigger}
                    triggerClassName={clsx(styles.pickerButton, !isMcpPermissionGateEnabled && styles.agentAfterAttach)}
                  />
                </ConsecutiveCAPI401sProvider>
              )}
            </>
          )}
          {(!isModelSelectionReady || shouldShowModelPicker) && (
            <div className={styles.pickerButtonWrapper}>
              <ModelPicker
                selectedModelId={selectedModelId}
                onUpdateModel={selectModel}
                vendorFilters={vendorFilters}
                isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
                showTriggerIcon={false}
                showLoadingState={!isModelSelectionReady}
                size="medium"
              />
            </div>
          )}
          {isModelSelectionReady && isReasoningEffortEnabled && (
            <ReasoningEffortPicker
              selectedReasoningEffort={selectedReasoningEffort}
              onUpdateReasoningEffort={setSelectedReasoningEffort}
              supportedReasoningEfforts={supportedReasoningEfforts}
              triggerClassName={styles.pickerButton}
            />
          )}
          {meter}
        </div>
      )}
      {!showCreateTaskStrip && (
        <TaskChatInputToolbar
          hideRepoSelector={hideRepoSelector}
          initialRepoNWO={initialNameWithOwner}
          initialBranch={initialBranch ?? branchParam ?? undefined}
          selectedRepo={selectedRepo || undefined}
          selectedBranch={selectedBranch}
          selectedAgent={selectedAgent}
          onRepoSelect={handleRepoSelect}
          onBranchSelect={(branch: string | undefined) => setSelectedBranch(branch || '')}
          onAgentSelect={(agent: SelectedAgent) => setSelectedAgent(agent)}
          agentSelectionEnabled={isSubAgentsEnabled && !isAhpEnvironment}
          textAreaRef={textAreaRef}
          isAtImageLimit={isAtImageLimit}
          currentPrompt={currentPrompt}
          currentUserLogin={resolvedUserLogin}
          hideImageAttach={isAhpEnvironment}
          disabled={isEnvironmentSubmitting}
          branchSelectorDisabled={isSandboxBranchSelectorDisabled(isSandboxEnvironment)}
        />
      )}
    </>
  )
}
